import { useState } from "react";
import { opApi, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, BookOpen, Copy, Boxes } from "lucide-react";

const priceDrivingGroup = (p) => (p.variant_options || []).find((g) => g.price_driving && (g.values || []).length);
const blankItem = () => ({ included: false, price: "", variant_prices: {}, override_mods: false, modifier_group_ids: [] });

export function MenusPanel({ products = [], modGroups = [], menus = [], primaryMenuId, reload }) {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState("");
  const [items, setItems] = useState({});
  const [q, setQ] = useState("");
  const [stockMenu, setStockMenu] = useState(null);
  const pname = (pid) => (products.find((p) => p.id === pid) || {}).name || "Item";
  const soldOutNow = (it) => !!it.sold_out_until && new Date(it.sold_out_until) > new Date();
  const dup = async (m) => {
    try { await opApi.post(`/tenant/menus/${m.id}/duplicate`); toast.success(`Duplicated "${m.name}"`); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const toggleSold = async (pid, makeSoldOut) => {
    try { const { data } = await opApi.put(`/tenant/menus/${stockMenu.id}/item/${pid}/soldout`, { sold_out: makeSoldOut }); setStockMenu(data); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const openNew = () => { setEditId(null); setName(""); setItems({}); setQ(""); setOpen(true); };
  const openEdit = (m) => {
    setEditId(m.id); setName(m.name || ""); setQ("");
    const map = {};
    (m.items || []).forEach((it) => {
      map[it.product_id] = {
        included: true,
        price: it.price == null ? "" : String(it.price),
        variant_prices: it.variant_prices || {},
        override_mods: it.modifier_group_ids != null,
        modifier_group_ids: it.modifier_group_ids || [],
      };
    });
    setItems(map); setOpen(true);
  };
  const it = (pid) => items[pid] || blankItem();
  const setItem = (pid, patch) => setItems((s) => ({ ...s, [pid]: { ...blankItem(), ...(s[pid] || {}), ...patch } }));
  const setVP = (pid, key, v) => setItem(pid, { variant_prices: { ...(it(pid).variant_prices || {}), [key]: v } });
  const toggleMod = (pid, gid) => {
    const cur = it(pid).modifier_group_ids || [];
    setItem(pid, { modifier_group_ids: cur.includes(gid) ? cur.filter((x) => x !== gid) : [...cur, gid] });
  };

  const save = async () => {
    if (!name.trim()) { toast.error("Name your menu"); return; }
    let sort = 0; const list = [];
    products.forEach((p) => {
      const i = items[p.id];
      if (!i || !i.included) return;
      const vp = {};
      Object.keys(i.variant_prices || {}).forEach((k) => { const v = i.variant_prices[k]; if (v !== "" && v != null) vp[k] = Number(v); });
      list.push({
        product_id: p.id, available: true, sort_order: sort++,
        price: (i.price === "" || i.price == null) ? null : Number(i.price),
        variant_prices: Object.keys(vp).length ? vp : null,
        modifier_group_ids: i.override_mods ? (i.modifier_group_ids || []) : null,
      });
    });
    if (!list.length) { toast.error("Add at least one product to the menu"); return; }
    try {
      if (editId) await opApi.put(`/tenant/menus/${editId}`, { name, description: "", items: list });
      else await opApi.post("/tenant/menus", { name, description: "", items: list });
      toast.success("Menu saved"); setOpen(false); reload();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (m) => {
    if (!window.confirm(`Delete "${m.name}"? Locations using it will fall back to the full catalog.`)) return;
    try { await opApi.delete(`/tenant/menus/${m.id}`); toast.success("Menu deleted"); reload(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const setPrimaryMenu = async (v) => {
    try { await opApi.put("/tenant/settings", { menu_id: v || "" }); toast.success("Main-shop menu updated"); reload(); }
    catch (e) { toast.error("Couldn't update the main-shop menu"); }
  };

  const filtered = products.filter((p) => !q || (p.name || "").toLowerCase().includes(q.toLowerCase()));
  const includedCount = Object.values(items).filter((i) => i.included).length;

  return (
    <div className="border border-border bg-muted/20 p-4 mb-6" data-testid="menus-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <BookOpen className="h-4 w-4 text-primary" />
          <div>
            <h3 className="font-display font-bold text-sm">Menus</h3>
            <p className="text-xs text-muted-foreground">Build a menu once (with its own prices) and assign it to a shop, truck or pop-up.</p>
          </div>
        </div>
        <Button size="sm" className="rounded-none gap-1" onClick={openNew} data-testid="add-menu-btn"><Plus className="h-4 w-4" /> New menu</Button>
      </div>

      <div className="flex items-center justify-between gap-3 border border-border bg-background px-3 py-2 mb-3">
        <Label className="text-xs font-medium">Main shop uses menu</Label>
        <select className="h-8 rounded-none border border-input bg-background px-2 text-sm min-w-[180px]"
          value={primaryMenuId || ""} onChange={(e) => setPrimaryMenu(e.target.value)} data-testid="primary-menu-select">
          <option value="">Full catalog (all products)</option>
          {menus.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
      </div>

      <div className="space-y-2">
        {menus.map((m) => (
          <div key={m.id} className="flex items-center justify-between border border-border bg-background p-2.5" data-testid={`menu-row-${m.id}`}>
            <div>
              <div className="text-sm font-medium">{m.name}</div>
              <div className="text-xs text-muted-foreground">{(m.items || []).filter((x) => x.available !== false).length} items</div>
            </div>
            <div className="flex items-center gap-1">
              <Button size="sm" variant="ghost" className="rounded-none gap-1 text-xs" onClick={() => setStockMenu(m)} data-testid={`stock-menu-${m.id}`}><Boxes className="h-3 w-3" /> Today's stock</Button>
              <Button size="sm" variant="ghost" className="rounded-none" title="Duplicate" onClick={() => dup(m)} data-testid={`dup-menu-${m.id}`}><Copy className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" className="rounded-none" onClick={() => openEdit(m)} data-testid={`edit-menu-${m.id}`}><Pencil className="h-3 w-3" /></Button>
              <Button size="sm" variant="ghost" className="rounded-none text-destructive" onClick={() => del(m)} data-testid={`del-menu-${m.id}`}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
        ))}
        {!menus.length && <div className="text-xs text-muted-foreground italic py-3 text-center">No menus yet — every location shows the full catalog.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-h-[88vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "New"} menu</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Menu name</Label><Input className="rounded-none mt-1" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Truck Menu" data-testid="menu-name-input" /></div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-muted-foreground">Pick products & set this menu's prices ({includedCount} selected)</Label>
            </div>
            <Input className="rounded-none" placeholder="Search products…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="menu-product-search" />
            <div className="max-h-[46vh] overflow-y-auto border border-border divide-y divide-border">
              {filtered.map((p) => {
                const i = it(p.id);
                const pdg = priceDrivingGroup(p);
                return (
                  <div key={p.id} className="p-2.5" data-testid={`menu-item-${p.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <Switch checked={!!i.included} onCheckedChange={(v) => setItem(p.id, { included: v })} data-testid={`menu-item-toggle-${p.id}`} />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">{p.name}</div>
                          <div className="text-[11px] text-muted-foreground">Catalog price ${Number(p.price || 0).toFixed(2)}</div>
                        </div>
                      </div>
                      {i.included && !pdg && (
                        <div className="flex items-center gap-1">
                          <span className="text-xs text-muted-foreground">$</span>
                          <Input type="number" step="0.01" className="rounded-none h-8 w-24" placeholder={Number(p.price || 0).toFixed(2)}
                            value={i.price} onChange={(e) => setItem(p.id, { price: e.target.value })} data-testid={`menu-item-price-${p.id}`} />
                        </div>
                      )}
                    </div>
                    {i.included && pdg && (
                      <div className="mt-2 pl-9 grid grid-cols-2 gap-2">
                        {(pdg.values || []).map((val) => {
                          const key = `${pdg.name}::${val.name}`;
                          return (
                            <div key={key} className="flex items-center gap-1">
                              <span className="text-[11px] text-muted-foreground w-16 truncate">{val.name}</span>
                              <span className="text-xs text-muted-foreground">$</span>
                              <Input type="number" step="0.01" className="rounded-none h-8" placeholder={Number(val.price || 0).toFixed(2)}
                                value={(i.variant_prices || {})[key] ?? ""} onChange={(e) => setVP(p.id, key, e.target.value)} data-testid={`menu-item-vp-${p.id}`} />
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {i.included && (
                      <div className="mt-2 pl-9">
                        <div className="flex items-center justify-between">
                          <Label className="text-[11px] font-normal text-muted-foreground">Customize add-ons for this menu</Label>
                          <Switch checked={!!i.override_mods} onCheckedChange={(v) => setItem(p.id, { override_mods: v, modifier_group_ids: v ? (i.modifier_group_ids.length ? i.modifier_group_ids : (p.modifier_group_ids || [])) : [] })} data-testid={`menu-item-mods-toggle-${p.id}`} />
                        </div>
                        {i.override_mods && (
                          <div className="flex flex-wrap gap-1.5 mt-1.5">
                            {modGroups.length ? modGroups.map((g) => (
                              <button key={g.id} type="button" onClick={() => toggleMod(p.id, g.id)}
                                className={`text-[11px] px-2 py-1 border rounded-none transition-colors ${(i.modifier_group_ids || []).includes(g.id) ? "border-primary bg-primary/10 font-semibold" : "border-border hover:border-primary/50"}`}
                                data-testid={`menu-item-modgrp-${p.id}-${g.id}`}>{g.name}</button>
                            )) : <span className="text-[11px] text-muted-foreground">No add-on groups yet.</span>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
              {!filtered.length && <div className="text-xs text-muted-foreground italic p-3 text-center">No products match.</div>}
            </div>
          </div>
          <DialogFooter><Button className="rounded-none" onClick={save} data-testid="save-menu-btn">Save menu</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!stockMenu} onOpenChange={(v) => !v && setStockMenu(null)}>
        <DialogContent className="rounded-none max-h-[85vh] overflow-y-auto sm:max-w-md" data-testid="stock-dialog">
          <DialogHeader><DialogTitle className="font-display">Today's stock — {stockMenu?.name}</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground -mt-1">Flip an item off to mark it <strong>sold out for today</strong>. It greys out on this menu instantly and comes back automatically tomorrow.</p>
          <div className="divide-y divide-border border border-border mt-1">
            {(stockMenu?.items || []).filter((it) => it.available !== false).map((it) => {
              const out = soldOutNow(it);
              return (
                <div key={it.product_id} className="flex items-center justify-between p-2.5" data-testid={`stock-item-${it.product_id}`}>
                  <div className={`text-sm ${out ? "line-through text-muted-foreground" : "font-medium"}`}>{pname(it.product_id)}</div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[11px] font-semibold ${out ? "text-destructive" : "text-green-600"}`}>{out ? "Sold out today" : "Available"}</span>
                    <Switch checked={!out} onCheckedChange={(v) => toggleSold(it.product_id, !v)} data-testid={`stock-toggle-${it.product_id}`} />
                  </div>
                </div>
              );
            })}
            {!(stockMenu?.items || []).filter((it) => it.available !== false).length && <div className="text-xs text-muted-foreground italic p-3 text-center">This menu has no products yet.</div>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
